import { useEffect, useId, useRef, useState } from 'react'

import { AccountItem } from './service/CopUserAccountService'

const DEFAULT_ACCOUNT: AccountItem = { id: 'NOSLA', name: 'No SLA', slaState: 'NoSLA' }

type Props = {
    accounts: AccountItem[]
    selectedId: string
    selectedName: string
    loading: boolean
    searching: boolean
    loadingMore: boolean
    hasMore: boolean
    placeholder: string
    searchPlaceholder: string
    showNoSla: boolean
    onSelect: (id: string, name: string) => void
    onSearchChange: (term: string) => void
    onLoadMore: () => void
}
export default function CopSearchableDropdown({
    accounts,
    selectedId,
    selectedName,
    loading,
    searching,
    loadingMore,
    hasMore,
    placeholder,
    searchPlaceholder,
    showNoSla,
    onSelect,
    onSearchChange,
    onLoadMore,
}: Readonly<Props>) {
    const triggerId = useId()
    const listboxId = useId()
    const searchInputId = useId()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [activeIndex, setActiveIndex] = useState(-1)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchRef = useRef<HTMLInputElement>(null)
    const optionsRef = useRef<HTMLUListElement>(null)

    const baseAccounts = showNoSla ? [DEFAULT_ACCOUNT, ...accounts] : accounts
    const allAccounts = showNoSla ? [DEFAULT_ACCOUNT, ...accounts] : accounts
    const filteredAccounts = search ? accounts : baseAccounts

    const selectedAccount = allAccounts.find((a) => a.id === selectedId)
        ?? (selectedId ? { id: selectedId, name: selectedName, slaState: '' } : undefined)
    const selectedLabel = selectedAccount?.name || (loading ? 'Loading accounts…' : placeholder)
    const disabled = loading || accounts.length === 0

    useEffect(() => {
        if (!open) return
        const onClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('click', onClick)
        return () => document.removeEventListener('click', onClick)
    }, [open])

    useEffect(() => {
        if (open) {
            setTimeout(() => searchRef.current?.focus(), 0)
        } else {
            setSearch('')
            onSearchChange('')
            setActiveIndex(-1)
        }
    }, [open])

    useEffect(() => {
        if (!open || !optionsRef.current || activeIndex < 0) return
        const el = optionsRef.current.children[activeIndex] as HTMLElement | undefined
        el?.scrollIntoView({ block: 'nearest' })
    }, [open, activeIndex])

    useEffect(() => {
        const listElement = optionsRef.current
        if (!listElement || !open) return

        const onOptionsScroll = () => {
            if (listElement.scrollTop + listElement.clientHeight >= listElement.scrollHeight - 40 && hasMore && !loadingMore && !searching) {
                onLoadMore()
            }
        }

        listElement.addEventListener('scroll', onOptionsScroll)
        return () => listElement.removeEventListener('scroll', onOptionsScroll)
    }, [open, hasMore, loadingMore, searching, onLoadMore])

    const openDropdown = () => {
        if (disabled) return
        setOpen(true)
        const idx = filteredAccounts.findIndex((a) => a.id === selectedId)
        setActiveIndex(Math.max(0, idx))
    }

    const clearSelection = (e: React.MouseEvent) => {
        e.stopPropagation()
        onSelect('', '')
        setOpen(true)
    }

    const choose = (account: AccountItem) => {
        onSelect(account.id, account.name)
        setOpen(false)
    }

    const focusOptionButton = (index: number) => {
        const buttons = optionsRef.current?.querySelectorAll<HTMLButtonElement>('.cop-ua__option-button')
        if (!buttons || index < 0 || index >= buttons.length) return
        buttons[index].focus()
    }

    const onSearchChange_input = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value)
        onSearchChange(e.target.value)
        setActiveIndex(0)
    }

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') { setOpen(false); return }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const nextIndex = Math.min(filteredAccounts.length - 1, Math.max(0, activeIndex + 1))
            setActiveIndex(nextIndex)
            focusOptionButton(nextIndex)
        }
        if (e.key === 'Enter' && activeIndex >= 0) {
            const acc = filteredAccounts[activeIndex]
            if (acc) choose(acc)
        }
    }

    return (
        <div
            className={`cop-ua__dropdown${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
            ref={dropdownRef}
        >
            {/* Trigger button */}
            <button
                id={triggerId}
                type="button"
                className="cop-ua__trigger"
                onClick={() => (open ? setOpen(false) : openDropdown())}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-label={selectedLabel}
            >
                <span className={`cop-ua__trigger-label${!selectedAccount ? ' is-placeholder' : ''}`}>
                    {selectedLabel}
                </span>
                {/* Clear button — shown whenever any account (including No SLA) is selected */}
                {selectedAccount && (
                    <button
                        type="button"
                        className="cop-ua__trigger-clear"
                        aria-label="Clear selection"
                        onClick={clearSelection}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
                {loading ? (
                    <span className="cop-ua__dropdown-spinner" aria-hidden="true" />
                ) : (
                    <svg className="cop-ua__chevron" width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div className="cop-ua__panel">
                    {/* Search */}
                    <div className="cop-ua__search-wrap">
                        {searching ? (
                            <span className="cop-ua__search-spinner" aria-label="Searching…" />
                        ) : (
                            <svg className="cop-ua__search-icon" width="14" height="14" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        )}
                        <input
                            id={searchInputId}
                            ref={searchRef}
                            type="text"
                            className="cop-ua__search"
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={onSearchChange_input}
                            onKeyDown={onSearchKeyDown}
                            aria-label="Search accounts"
                            autoComplete="off"
                        />
                        {search && (
                            <button
                                type="button"
                                className="cop-ua__search-clear"
                                aria-label="Clear search"
                                onClick={() => { setSearch(''); onSearchChange(''); setActiveIndex(0); searchRef.current?.focus() }}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Options list — spinner replaced the search icon above during search */}
                    <ul
                        id={listboxId}
                        className="cop-ua__options"
                        ref={optionsRef}
                        tabIndex={-1}
                        aria-labelledby={triggerId}
                        aria-busy={searching || loadingMore}
                        aria-activedescendant={activeIndex >= 0 ? `cop-ua-opt-${activeIndex}` : undefined}
                    >
                        {filteredAccounts.length === 0 ? (
                            <li className="cop-ua__option cop-ua__option--empty">No results found</li>
                        ) : (
                            filteredAccounts.map((account, index) => (
                                <li
                                    key={account.id}
                                    id={`cop-ua-opt-${index}`}
                                    className={[
                                        'cop-ua__option',
                                        index === activeIndex ? 'is-active' : '',
                                        account.id === selectedId ? 'is-selected' : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <button
                                        type="button"
                                        className="cop-ua__option-button"
                                        aria-pressed={account.id === selectedId}
                                        onClick={() => choose(account)}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                e.preventDefault()
                                                setOpen(false)
                                                return
                                            }
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault()
                                                const nextIndex = Math.min(filteredAccounts.length - 1, index + 1)
                                                setActiveIndex(nextIndex)
                                                focusOptionButton(nextIndex)
                                                return
                                            }
                                            if (e.key === 'ArrowUp') {
                                                e.preventDefault()
                                                if (index <= 0) {
                                                    searchRef.current?.focus()
                                                    return
                                                }
                                                const previousIndex = Math.max(0, index - 1)
                                                setActiveIndex(previousIndex)
                                                focusOptionButton(previousIndex)
                                            }
                                        }}
                                    >
                                        {account.name} ({account.id})
                                    </button>
                                </li>
                            ))
                        )}
                        {loadingMore && (
                            <li className="cop-ua__option cop-ua__option--empty">Loading more…</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    )
}
